import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { FcGoogle } from "react-icons/fc";
import { FaMicrosoft } from "react-icons/fa";
import Spinner from "../components/Spinner";
import { authApi } from "../api/authApi";
import { ApiError, getApiErrorMessage } from "../api/apiClient";
import strivreLogo from "../assets/images/strivre-logo.svg";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  async function handleSubmit(
    e: React.FormEvent
  ) {
    e.preventDefault();

    setIsLoading(true);
    setError("");

    try {
      const res = await authApi.login(
        email,
        password
      );

      login(
        res.token,
        res.user,
        res.subscription
      );

      navigate("/");
    } catch (err) {
      if (
        err instanceof ApiError &&
        err.status === 403 &&
        err.code === "PASSWORD_EXPIRED"
      ) {
        navigate("/password-expired", {
          replace: true,
          state: {
            email,
          },
        });

        return;
      }

      setError(
        getApiErrorMessage(
          err,
          "Invalid email or password. Please try again."
        )
      );
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="w-full max-w-md p-8 card bg-white shadow-xl border border-strivre-lightnavy animate-fade-in">
      {/* Logo & Header */}
      <div className="text-center mb-8">
        <div className="flex justify-center mb-5">
          <img
            src={strivreLogo}
            alt="Strivre"
            className="h-20 w-auto max-w-[240px] object-contain"
          />
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1" htmlFor="email">
            Email Address
          </label>
          <input
            id="email"
            type="email"
            placeholder="name@company.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
            className="input-base w-full"
            disabled={isLoading}
            required
          />
        </div>

        <div>
          <label
            className="block text-sm font-semibold text-slate-700 mb-1"
            htmlFor="password"
          >
            Password
          </label>

          <input
            id="password"
            type="password"
            placeholder="••••••••"
            value={password}
            onChange={e => setPassword(e.target.value)}
            className="input-base w-full"
            disabled={isLoading}
            required
          />

          <div className="mt-2 flex justify-end">
            <Link
              to="/forgot-password"
              className="text-sm font-semibold text-strivre-burgundy hover:underline"
            >
              Forgot password?
            </Link>
          </div>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-100 text-red-600 text-sm rounded-lg animate-shake">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary w-full py-3 flex items-center justify-center gap-2"
        >
          {isLoading ? (
            <>
              <Spinner size="h-5 w-5" />
              Signing in...
            </>
          ) : (
            "Sign In"
          )}
        </button>

        {/* <div className="mt-8 text-center border-slate-100 pt-6">
          <p className="text-slate-600 text-sm">
            Don't have an account?{" "}
            <Link to="/register" className="text-strivre-burgundy font-bold hover:underline">Sign Up</Link>
          </p>
        </div> */}
      </form>

      <div className="relative my-8">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-strivre-lightnavy" />
        </div>

        <div className="relative flex justify-center">
          <span className="bg-white px-4 text-xs font-semibold uppercase tracking-wider text-slate-400">
            More sign-in options
          </span>
        </div>
      </div>

      <div className="space-y-3">
        <button
          type="button"
          disabled
          className="relative w-full flex items-center justify-center gap-3 rounded-xl border border-strivre-lightnavy bg-slate-50 px-4 py-3 text-slate-400 font-medium cursor-not-allowed"
        >
          <FcGoogle size={20} className="grayscale opacity-60" />

          <span>Continue with Google</span>

          <span className="absolute right-3 rounded-full bg-strivre-lightburgundy px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-strivre-burgundy">
            Coming Soon
          </span>
        </button>

        <button
          type="button"
          disabled
          className="relative w-full flex items-center justify-center gap-3 rounded-xl border border-strivre-lightnavy bg-slate-50 px-4 py-3 text-slate-400 font-medium cursor-not-allowed"
        >
          <FaMicrosoft size={18} className="text-slate-400 opacity-60" />

          <span>Continue with Microsoft 365</span>

          <span className="absolute right-3 rounded-full bg-strivre-lightburgundy px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-strivre-burgundy">
            Coming Soon
          </span>
        </button>

        <p className="pt-1 text-center text-xs text-slate-400">
          Google and Microsoft 365 sign-in will be available soon.
        </p>
      </div>
    </div>
  );
}
